import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  LayoutPanelLeft,
  Maximize2,
} from "lucide-react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { OrgBrand } from "@/components/OrgBrand";
import type { SeriesRailState } from "@/components/tests/TestCaseViewer";
import { Movable } from "@/components/viewer/Movable";
import { RoomViewer } from "@/components/viewer/RoomViewer";
import { TestCountdown } from "@/components/tests/TestCountdown";
import { CandidateApplicationForm } from "@/components/tests/CandidateApplicationForm";
import { TestRecorder } from "@/components/tests/TestRecorder";
import { TestStartGate } from "@/components/tests/TestStartGate";
import { AppDialog } from "@/components/ui/AppDialog";
import { formatCountdown, useTestCountdown } from "@/hooks/useTestCountdown";
import { useToast } from "@/hooks/use-toast";
import {
  beginAssessment,
  completeIntro,
  exitAssessment,
  finishDryRun,
  heartbeat,
  openCase,
  recordIntegrityEvent,
  startDryRun,
  submitAttempt,
  useAttempt,
  type AttemptCase,
} from "@/api/tests-api";

const HEARTBEAT_MS = 30_000;

/**
 * The sitting.
 *
 * Four states, in order, and the clock only runs in the last two:
 *
 *   intro  →  start gate (+ optional practice run)  →  assessment  →  done
 *
 * The gate is the point. Opening the exam used to start the countdown,
 * so a candidate lost assessment time to reading the instructions.
 * Nothing runs now until they deliberately press "Start the test".
 */
export function TestAttemptPage() {
  const { attemptId = "" } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const attempt = useAttempt(attemptId);
  const data = attempt.data;

  const [selected, setSelected] = useState<string | null>(null);
  const [showIntro, setShowIntro] = useState(true);
  const [busy, setBusy] = useState(false);
  const [finished, setFinished] = useState(false);
  // Which irreversible action is being confirmed, if any. Replaces two
  // `window.confirm` calls -- see AppDialog for why.
  const [confirming, setConfirming] = useState<"submit" | "exit" | null>(null);
  // The sealed case a recording just landed for, which drives the
  // "recorded — what next?" dialog. Null when that dialog is closed.
  const [recordedFor, setRecordedFor] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setShowIntro(!data.intro_completed);
    if (data.started && data.status !== "IN_PROGRESS") setFinished(true);
  }, [data]);

  const onExpire = useCallback(() => {
    setFinished(true);
    toast({
      title: "Time is up",
      description: "Your assessment has ended and your work has been saved.",
    });
  }, [toast]);

  const inDryRun = !!data?.dry_run_started && !data?.dry_run_finished;
  const inAssessment = !!data?.started && !finished;

  // Whichever clock is running drives the countdown — only one ever is,
  // because beginning the assessment closes any open practice run.
  const liveSeconds = inAssessment
    ? (data?.seconds_remaining ?? null)
    : inDryRun
      ? (data?.dry_run_seconds_remaining ?? null)
      : null;

  const countdown = useTestCountdown(
    liveSeconds,
    inAssessment ? onExpire : undefined,
    // The two clocks are different deadlines; switching between them has
    // to re-anchor, or the assessment inherits the practice countdown.
    inAssessment ? "assessment" : inDryRun ? "practice" : "idle",
  );
  const syncRef = useRef(countdown.syncFromServer);
  useEffect(() => {
    syncRef.current = countdown.syncFromServer;
  }, [countdown.syncFromServer]);

  useEffect(() => {
    if (!attemptId || !inAssessment) return undefined;
    const id = window.setInterval(() => {
      void heartbeat(attemptId)
        .then((tick) => {
          syncRef.current(tick.seconds_remaining);
          if (tick.status !== "IN_PROGRESS") setFinished(true);
        })
        .catch(() => {
          /* transient — the next beat retries */
        });
    }, HEARTBEAT_MS);
    return () => window.clearInterval(id);
  }, [attemptId, inAssessment]);

  // Integrity signals: context for a reviewer, never a gate. Only during
  // the assessment — nobody cares what a candidate does while practising.
  useEffect(() => {
    if (!attemptId || !inAssessment) return undefined;
    const onHide = () => {
      if (document.visibilityState === "hidden") {
        void recordIntegrityEvent(attemptId, "tab_hidden");
      }
    };
    const onBlur = () => void recordIntegrityEvent(attemptId, "window_blur");
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("blur", onBlur);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("blur", onBlur);
    };
  }, [attemptId, inAssessment]);

  const cases = useMemo<AttemptCase[]>(
    () => (inDryRun ? (data?.dry_run_cases ?? []) : (data?.cases ?? [])),
    [data, inDryRun],
  );
  // 1 application + (practice) + assessment. `has_dry_run` is per exam.
  const totalSteps = data?.has_dry_run ? 3 : 2;
  const assessmentStep = totalSteps;

  const current = cases.find((c) => c.assignment_case_id === selected) ?? null;
  const recordedCount = cases.filter((c) => c.answered).length;
  const unrecorded = cases.length - recordedCount;
  const caseIndex = current
    ? cases.findIndex((c) => c.assignment_case_id === current.assignment_case_id)
    : -1;

  // The SERIES of the case being read, reported up by the viewer rather
  // than derived a second time here. Null until it has rendered once.
  const [rail, setRail] = useState<SeriesRailState | null>(null);
  // The reading room's dock while it is full screen: the report pane moves
  // into it, so the recorder and the clock stay in reach.
  const [dock, setDock] = useState<HTMLElement | null>(null);
  // The bar across the top of the room while its dock is FOLDED: the
  // report row moves there instead, so the recorder never leaves reach.
  const [dockBar, setDockBar] = useState<HTMLElement | null>(null);
  const docked = !!dock && !dockBar;
  // "Focus": the room full screen, held here so the top bar can open it.
  const [focus, setFocus] = useState(false);
  // Which panes are showing. Per sitting and deliberately not stored: a
  // candidate who folded the list on one exam starts the next with
  // everything in view.
  const [showCases, setShowCases] = useState(true);
  const [showSeries, setShowSeries] = useState(true);
  const [showGuide, setShowGuide] = useState(false);
  const [layoutOpen, setLayoutOpen] = useState(false);
  const layoutRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!layoutOpen) return undefined;
    const away = (e: MouseEvent) => {
      if (!layoutRef.current?.contains(e.target as Node)) setLayoutOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [layoutOpen]);

  // HOW MUCH TIME PER CASE STILL TO DO, which is the question a candidate
  // is really asking of the clock. Whole minutes, floored: rounding up
  // would tell somebody with 70 seconds and two cases that they have a
  // minute each.
  const paceLine =
    unrecorded > 0 && countdown.secondsRemaining > 0
      ? `~${Math.max(
          1,
          Math.floor(countdown.secondsRemaining / 60 / unrecorded),
        )} min per case`
      : unrecorded === 0
        ? "All cases recorded"
        : "";

  // The first-run note, dismissed per sitting. sessionStorage, not
  // local: a candidate who returns to a second exam is a first-timer on
  // it again, and a candidate who reloads mid-sitting is not.
  const [showHelp, setShowHelp] = useState(true);

  // The "recorded — what next?" dialog reads off the CASE LIST rather
  // than off the id alone, so it closes by itself if the phase changes
  // underneath it (finishing practice swaps the whole list).
  const justRecorded =
    cases.find((c) => c.assignment_case_id === recordedFor) ?? null;
  const nextCase = justRecorded
    ? (cases[cases.findIndex(
        (c) => c.assignment_case_id === justRecorded.assignment_case_id,
      ) + 1] ?? null)
    : null;

  // Land ON the first case rather than on an empty panel.
  //
  // Two jobs in one effect, and they have to be one: changing phase must
  // not leave a case from the OTHER phase selected, and arriving with
  // nothing selected showed a blank right-hand side that looked broken —
  // a candidate under a clock had to work out that the list was
  // clickable. Selecting the first case of whichever phase is running
  // covers both, and re-runs when `cases` changes because the practice
  // and assessment lists arrive at different moments.
  useEffect(() => {
    if (cases.length === 0) {
      setSelected(null);
      return;
    }
    const stillHere = cases.some((c) => c.assignment_case_id === selected);
    if (stillHere) return;
    // Land on the first case STILL TO RECORD, not blindly on the first.
    // A candidate resuming after a closed tab or a dropped connection
    // came back to case 1 with a tick already on it, and had to work out
    // for themselves where they had got to -- under a clock that never
    // stopped. Falls back to the first case when everything is done,
    // which is the right place to start a review pass.
    const next = cases.find((c) => !c.answered) ?? cases[0]!;
    setSelected(next.assignment_case_id);
  }, [cases, selected]);

  const act = async (fn: () => Promise<unknown>, failure: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      await attempt.refetch();
    } catch (err) {
      // SHOW THE SERVER'S REASON. A bare `catch {}` here turned a
      // specific 422 -- "Please answer: <a question the candidate could
      // not see>" -- into "Could not move on to the assessment", and
      // that cost a diagnosis. The fallback is for a network failure,
      // which genuinely has nothing to say.
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? failure;
      toast({ title: detail, variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const pickCase = async (row: AttemptCase) => {
    setSelected(row.assignment_case_id);
    try {
      await openCase(attemptId, row.assignment_case_id);
    } catch {
      /* opening is a bookmark, not a gate */
    }
  };

  /** The application form has already saved itself; this only marks the
   *  step done on the attempt, which is what survives a reload. */
  const finishIntro = () =>
    act(async () => {
      await completeIntro(attemptId);
      setShowIntro(false);
    }, "Could not move on to the assessment");

  const doSubmit = () =>
    act(async () => {
      await submitAttempt(attemptId);
      setFinished(true);
    }, "Could not submit");

  const doExit = () =>
    act(async () => {
      await exitAssessment(attemptId);
      setFinished(true);
    }, "Could not exit");

  /**
   * A take landed. Refetch first, THEN offer where to go next.
   *
   * Order matters: the dialog names the next case and, on the last one,
   * offers to submit -- both read off a case list that must already
   * include the tick this recording just earned. Offering "submit" while
   * the panel behind still says "1 of 2 still to record" is the kind of
   * disagreement that makes a candidate under a clock stop and re-check.
   */
  const handleRecorded = async (assignmentCaseId: string) => {
    await attempt.refetch();
    setRecordedFor(assignmentCaseId);
  };

  if (attempt.isLoading) {
    return (
      <Shell>
        <p data-cy="attempt-loading" className="text-sm text-foreground-muted">
          Loading your assessment…
        </p>
      </Shell>
    );
  }

  if (attempt.isError || !data) {
    return (
      <Shell>
        <div className="text-center">
          <p data-cy="attempt-error" className="text-sm text-status-urgent">
            This assessment could not be opened.
          </p>
          <button
            type="button"
            onClick={() => navigate("/tests")}
            className="sk-btn sk-btn-primary mt-4"
          >
            Back to my assessments
          </button>
        </div>
      </Shell>
    );
  }

  // A SCREENING ROUND MUST NEVER RENDER THIS SCREEN.
  //
  // The candidate list already sends each round to its own page, but a
  // URL is a URL: a bookmark, the back button, or a tab left open from
  // a previous sitting all land here. What it would draw is step one --
  // the nine-section application form, which a screening round has no
  // step for and whose answers it has nowhere to store.
  //
  // `replace` so the back button does not bounce between the two.
  // The server refuses `complete_intro` for this round as well; this is
  // the half that stops the form ever being on screen to fill in.
  if (data.kind === "VIDEO_ROUND") {
    return <Navigate to={`/tests/${attemptId}/screening`} replace />;
  }

  if (finished) {
    return (
      <Shell>
        <div
          data-cy="attempt-finished"
          className="max-w-md rounded-xl border border-border bg-background-card p-8 text-center"
        >
          <h1 className="text-lg font-semibold">Assessment complete</h1>
          <p className="mt-2 text-sm text-foreground-muted">
            Thank you. Your work has been submitted and is with the clinic for
            review. There is nothing further for you to do here.
          </p>
          <button
            type="button"
            data-cy="btn-back-to-tests"
            onClick={() => navigate("/tests")}
            className="sk-btn sk-btn-primary mt-6"
          >
            Back to my assessments
          </button>
        </div>
      </Shell>
    );
  }

  // ---- step 1: the application form ----------------------------------
  //
  // STATIC, and saving to its own table. The dynamic question engine this
  // replaced is still on disk and still works -- see CLAUDE.md, "Inert
  // code kept on purpose".
  if (showIntro) {
    return (
      <main
        className="flex min-h-screen flex-col bg-background text-foreground"
        data-cy="test-attempt-page"
      >
        <PlainHeader title={data.title} step={1} total={totalSteps} />
        <section className="flex-1 px-6 py-8">
          <CandidateApplicationForm
            prompt={data.intro_prompt}
            onContinue={() => void finishIntro()}
          />
        </section>
      </main>
    );
  }

  // ---- step 2: the gate. Nothing is running here. ---------------------
  if (!inAssessment && !inDryRun) {
    return (
      <main
        className="flex min-h-screen flex-col bg-background text-foreground"
        data-cy="test-attempt-page"
      >
        <PlainHeader title={data.title} />
        <TestStartGate
          attempt={data}
          busy={busy}
          onStartDryRun={() =>
            void act(
              () => startDryRun(attemptId),
              "Could not start the practice run",
            )
          }
          onSkipDryRun={() =>
            void act(() => finishDryRun(attemptId, true), "Could not skip")
          }
          onBeginAssessment={() =>
            void act(
              () => beginAssessment(attemptId),
              "Could not start the test",
            )
          }
        />
      </main>
    );
  }

  // ---- step 3: working — practising, or being assessed ---------------
  //
  // THE READING SCREEN, re-laid-out 2026-10-07 to the layout the user
  // supplied as a screenshot (no design file exists for it): a top bar
  // carrying where the candidate is and the clock, the cases and the
  // series in one collapsible column on the left, and the rest of the
  // screen given to the reading room, with the recorder as one row under
  // it. Replaces the four-pane screen approved 2026-09-13 -- knowingly,
  // and it owes the client a re-approval.
  const activeSeries = rail ? (rail.series[rail.index] ?? null) : null;
  const hiddenPanes = [!showCases, !showSeries, !showGuide].filter(Boolean).length;
  const reportsLeft = `${unrecorded} ${unrecorded === 1 ? "case still needs" : "cases still need"} a report`;

  return (
    <main
      className="flex h-screen min-h-[560px] flex-col overflow-hidden bg-sitting-ground text-foreground"
      data-cy="test-attempt-page"
    >
      <header
        data-cy="topbar"
        className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-border bg-background-card px-5 py-2.5"
      >
        {/* The logo when the organisation has uploaded one, its name when
            it has not. OrgBrand decides; nothing here hard-codes a
            practice. */}
        <OrgBrand variant="topbar" />
        <span aria-hidden className="h-8 w-px bg-border" />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2.5">
            <p className="sk-sit-strong truncate text-foreground-heading">
              {data.title}
            </p>
            {inDryRun && (
              <span
                data-cy="practice-badge"
                className="shrink-0 rounded-full bg-status-new-bg px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-status-new"
              >
                Practice — not counted
              </span>
            )}
          </div>
          <p className="sk-sit-meta mt-0.5 text-foreground-muted">
            <span data-cy="step-badge">
              Step {inDryRun ? 2 : assessmentStep} of {totalSteps}
            </span>
            {current && (
              <>
                {" · "}
                <span data-cy="position-line">
                  Case {caseIndex + 1} of {cases.length} ·{" "}
                  {unrecorded === 0
                    ? "nothing left to record"
                    : `${unrecorded} still to record`}
                </span>
              </>
            )}
          </p>
          {/* One bar per case, not a single meter: a candidate sees
              WHICH are done rather than a percentage. */}
          <div
            data-cy="where-you-are"
            className="mt-1.5 flex max-w-[360px] gap-[3px]"
            aria-hidden
          >
            {cases.map((row) => (
              <span
                key={row.assignment_case_id}
                data-answered={row.answered ? "true" : "false"}
                className={[
                  "h-1 flex-1 rounded-full",
                  row.answered
                    ? "bg-accent"
                    : selected === row.assignment_case_id
                      ? "bg-status-new"
                      : "bg-border",
                ].join(" ")}
              />
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            data-cy="btn-focus"
            disabled={!current}
            onClick={() => setFocus(true)}
            title="The images full screen, with your report and the clock beside them"
            className="sk-sit-row inline-flex items-center gap-2 rounded-lg border border-border-strong bg-background-card px-3 py-2 text-foreground hover:bg-table-row-hover disabled:opacity-40"
          >
            <Maximize2 aria-hidden className="h-4 w-4" />
            Focus
          </button>
          <div ref={layoutRef} className="relative">
            <button
              type="button"
              data-cy="btn-layout"
              aria-expanded={layoutOpen}
              onClick={() => setLayoutOpen((o) => !o)}
              className="sk-sit-row inline-flex items-center gap-2 rounded-lg border border-border-strong bg-background-card px-3 py-2 text-foreground hover:bg-table-row-hover"
            >
              <LayoutPanelLeft aria-hidden className="h-4 w-4" />
              Layout
              {hiddenPanes > 0 && (
                <span
                  data-cy="layout-hidden-count"
                  className="rounded-full bg-background-subtle px-2 py-px text-[11px] font-semibold text-foreground-muted"
                >
                  {hiddenPanes} hidden
                </span>
              )}
            </button>
            {layoutOpen && (
              <div
                data-cy="layout-menu"
                className="absolute right-0 top-full z-30 mt-1.5 w-[240px] rounded-xl border border-border bg-background-card p-2 shadow-theme-md"
              >
                <p className="sk-sit-meta px-2 pb-1.5 pt-1 font-semibold uppercase tracking-[0.06em] text-foreground-muted">
                  Show
                </p>
                {(
                  [
                    ["cases", "Case list", showCases, setShowCases],
                    ["series", "Series", showSeries, setShowSeries],
                    ["guide", "Report guide", showGuide, setShowGuide],
                  ] as const
                ).map(([key, label, on, set]) => (
                  <label
                    key={key}
                    className="sk-sit-row flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 text-foreground hover:bg-table-row-hover"
                  >
                    <input
                      type="checkbox"
                      data-cy={`layout-${key}`}
                      checked={on}
                      onChange={(e) => set(e.target.checked)}
                      className="h-4 w-4 accent-[rgb(var(--status-new))]"
                    />
                    {label}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3.5 border-l border-border pl-5">
          <div className="text-right">
            <p className="text-[0.75rem] font-semibold uppercase tracking-[0.08em] text-foreground-muted">
              {inDryRun ? "Practice time" : "Time left"}
            </p>
            {paceLine && (
              <p data-cy="pace-line" className="sk-sit-note text-foreground-muted">
                {paceLine}
              </p>
            )}
          </div>
          <TestCountdown
            secondsRemaining={countdown.secondsRemaining}
            muted={inDryRun}
          />
        </div>
      </header>

      {/* FIRST TIME HERE. Dismissed for the sitting, not for ever: a
          candidate sitting a second exam is a first-timer on it again,
          and one who reloads mid-sitting is not being told anything they
          have not just read. */}
      {showHelp && !inDryRun && (
        <div
          data-cy="sitting-help"
          className="flex items-start gap-3.5 border-b border-sitting-note-border bg-sitting-note-bg px-5 py-2.5"
        >
          <span
            aria-hidden
            className="mt-0.5 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-status-new text-sm font-bold text-white"
          >
            i
          </span>
          <p className="sk-sit-body min-w-0 flex-1 text-foreground">
            <strong className="font-bold">First time sitting this exam?</strong>{" "}
            Work down the case list on the left. For each case, open the series
            you need, then record one spoken report. Nothing is final until you
            press Submit — you can return to any case.
          </p>
          <button
            type="button"
            data-cy="btn-dismiss-help"
            onClick={() => setShowHelp(false)}
            className="sk-sit-row shrink-0 rounded-lg border border-status-new/40 bg-background-card px-3 py-1.5 text-status-new hover:bg-table-row-hover"
          >
            Got it
          </button>
        </div>
      )}

      <section
        data-cy="sitting-columns"
        className="flex min-h-0 flex-1 gap-3 p-3"
      >
        {/* ---- Cases, series, and the way out ------------------------- */}
        {showCases ? (
          <nav
            data-cy="test-case-nav"
            className="flex w-[272px] shrink-0 flex-col overflow-hidden rounded-xl border border-border bg-background-card"
          >
            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="flex items-center gap-2 px-3.5 pb-2 pt-3">
                <p className="sk-sit-meta min-w-0 flex-1 font-bold uppercase tracking-[0.06em] text-foreground-muted">
                  {inDryRun ? "Practice cases" : "Cases"}
                  {" · "}
                  <span data-cy="cases-recorded-line">
                    {recordedCount} of {cases.length} recorded
                  </span>
                </p>
                <button
                  type="button"
                  data-cy="btn-hide-cases"
                  onClick={() => setShowCases(false)}
                  title="Fold the case list"
                  aria-label="Fold the case list"
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-foreground-muted hover:bg-table-row-hover hover:text-foreground"
                >
                  <ChevronLeft aria-hidden className="h-4 w-4" />
                </button>
              </div>
              <ul className="flex flex-col gap-1.5 px-2.5 pb-3">
                {cases.map((row) => {
                  const isActive = selected === row.assignment_case_id;
                  return (
                    <li key={row.assignment_case_id}>
                      <button
                        type="button"
                        data-cy="test-case-item"
                        onClick={() => void pickCase(row)}
                        className={[
                          "flex w-full items-start gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors",
                          isActive
                            ? "border-status-new bg-status-new-bg"
                            : "border-border hover:bg-table-row-hover",
                        ].join(" ")}
                      >
                        {/* GREEN IS RECORDED, BLUE IS THE ONE OPEN, and
                            they are different questions. Tinting the open
                            case green made an unrecorded case somebody
                            happened to be reading look finished. */}
                        <span
                          className={[
                            "mt-px flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full [font-size:12px] font-bold tabular-nums",
                            row.answered
                              ? "bg-accent text-accent-text"
                              : isActive
                                ? "bg-status-new text-white"
                                : "bg-background-subtle text-foreground-muted",
                          ].join(" ")}
                        >
                          {row.answered ? (
                            <Check aria-hidden className="h-3 w-3" />
                          ) : (
                            row.position + 1
                          )}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span
                            data-cy="case-title"
                            className="sk-sit-row block truncate text-foreground"
                          >
                            {row.title}
                          </span>
                          {(row.answered || !inDryRun) && (
                            <span
                              data-cy="case-status"
                              className={[
                                "sk-sit-meta mt-0.5 block",
                                row.answered
                                  ? "font-semibold text-accent-hover"
                                  : "text-foreground-muted",
                              ].join(" ")}
                            >
                              {row.answered ? "Report recorded" : "Not recorded yet"}
                            </span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>

              {/* NO GROUPING AND NO SECOND LINE. DICOM gives us exactly
                  one string per series -- SeriesDescription -- and nothing
                  that says which group it is in, so a heading or a
                  friendly name would have to be invented. An invented
                  label on a reading screen is worse than a raw one. */}
              {showSeries && rail && rail.series.length > 0 && (
                <div data-cy="series-pane" className="border-t border-border pt-3">
                  <p className="sk-sit-meta px-3.5 font-bold uppercase tracking-[0.06em] text-foreground-muted">
                    Series
                  </p>
                  <p
                    data-cy="series-summary"
                    className="sk-sit-meta mt-1 px-3.5 text-foreground-muted"
                  >
                    {rail.unseen === 0
                      ? `All ${rail.series.length} opened`
                      : `${rail.unseen} of ${rail.series.length} not opened yet`}
                  </p>
                  <ul className="px-2.5 pb-2 pt-1.5">
                    {rail.series.map((s, i) => (
                      <li key={s.key}>
                        <button
                          type="button"
                          data-cy="series-tab"
                          data-seen={s.seen ? "true" : "false"}
                          onClick={() => rail.select(i)}
                          title={s.seen ? s.label : `${s.label} — not opened yet`}
                          className={[
                            "my-0.5 flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left transition-colors",
                            i === rail.index
                              ? "border-status-new bg-status-new-bg"
                              : "border-transparent hover:bg-table-row-hover",
                          ].join(" ")}
                        >
                          <span
                            aria-hidden
                            className={[
                              "flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full [font-size:11px] font-bold leading-none",
                              s.seen
                                ? "bg-accent text-accent-text"
                                : "border-[1.5px] border-border-strong",
                            ].join(" ")}
                          >
                            {s.seen ? "✓" : ""}
                          </span>
                          <span className="min-w-0 flex-1 truncate font-mono [font-size:12.5px] font-medium text-foreground">
                            {s.label}
                          </span>
                          <span className="sk-sit-meta shrink-0 tabular-nums text-foreground-muted">
                            {s.count}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                  <p className="sk-sit-meta px-3.5 pb-3 text-foreground-muted">
                    A tick means you have opened that series. Ticks are not graded.
                  </p>
                </div>
              )}
            </div>

            <div className="border-t border-border p-3">
              {inDryRun ? (
                <>
                  <button
                    type="button"
                    data-cy="btn-finish-dry-run"
                    disabled={busy}
                    onClick={() =>
                      void act(
                        () => finishDryRun(attemptId, false),
                        "Could not end the practice run",
                      )
                    }
                    className="sk-sit-action w-full rounded-[10px] bg-accent px-3.5 py-3 text-accent-text hover:bg-accent-hover disabled:opacity-50"
                  >
                    Finish practice
                  </button>
                  <p className="sk-sit-note mt-2 text-foreground-muted">
                    None of this is counted. You go back to the start screen
                    and begin the assessment when you are ready.
                  </p>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    data-cy="btn-submit-attempt"
                    disabled={busy}
                    onClick={() => setConfirming("submit")}
                    className="sk-sit-action w-full rounded-[10px] bg-accent px-3.5 py-3 text-accent-text hover:bg-accent-hover disabled:opacity-50"
                  >
                    Review &amp; submit
                  </button>
                  <div className="mt-2 flex items-baseline gap-2">
                    <p
                      data-cy="submit-hint"
                      className="sk-sit-note min-w-0 flex-1 text-foreground-muted"
                    >
                      {unrecorded === 0 ? "All reports recorded" : reportsLeft}
                    </p>
                    {/* A LINK, not a second button. Leaving is quieter
                        than finishing on purpose -- two buttons of equal
                        weight invite the wrong one. */}
                    <button
                      type="button"
                      data-cy="btn-exit-attempt"
                      disabled={busy}
                      onClick={() => setConfirming("exit")}
                      title="Ends the assessment — you cannot return to it"
                      className="sk-sit-note shrink-0 text-foreground-muted underline underline-offset-2 hover:text-foreground disabled:opacity-50"
                    >
                      Leave
                    </button>
                  </div>
                </>
              )}
            </div>
          </nav>
        ) : (
          // FOLDED, the column keeps a way to every case: a numbered
          // button each, coloured as in the list.
          <nav
            data-cy="cases-folded"
            className="flex w-[52px] shrink-0 flex-col items-center gap-1.5 overflow-y-auto rounded-xl border border-border bg-background-card py-2.5"
          >
            <button
              type="button"
              data-cy="btn-show-cases"
              onClick={() => setShowCases(true)}
              title="Show the case list"
              aria-label="Show the case list"
              className="mb-1 flex h-8 w-8 items-center justify-center rounded-md text-foreground-muted hover:bg-table-row-hover hover:text-foreground"
            >
              <ChevronRight aria-hidden className="h-4 w-4" />
            </button>
            {cases.map((row) => (
              <button
                key={row.assignment_case_id}
                type="button"
                onClick={() => void pickCase(row)}
                title={`${row.title} — ${row.answered ? "report recorded" : "not recorded yet"}`}
                className={[
                  "flex h-8 w-8 items-center justify-center rounded-full [font-size:12px] font-bold tabular-nums",
                  row.answered
                    ? "bg-accent text-accent-text"
                    : selected === row.assignment_case_id
                      ? "bg-status-new text-white"
                      : "bg-background-subtle text-foreground-muted",
                ].join(" ")}
              >
                {row.position + 1}
              </button>
            ))}
          </nav>
        )}

        {/* ---- The case: its heading, the room, the recorder ---------- */}
        <div className="flex min-w-0 flex-1 flex-col gap-2.5">
          {current ? (
            <>
              <div
                data-cy="test-case-detail"
                className="flex flex-wrap items-center gap-x-4 gap-y-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-baseline gap-3">
                    <h1 className="sk-sit-title truncate text-foreground-heading">
                      {current.title}
                    </h1>
                    {activeSeries && (
                      <span
                        data-cy="active-series"
                        className="min-w-0 truncate font-mono [font-size:13px] text-foreground-muted"
                      >
                        {activeSeries.label}
                      </span>
                    )}
                  </div>
                  {/* THE BRIEF, when the case carries one, where it cannot
                      be missed. Absent, this draws nothing. */}
                  {current.clinical_history ? (
                    <p
                      data-cy="case-history"
                      className="sk-sit-body mt-0.5 whitespace-pre-line text-foreground"
                    >
                      <span className="font-semibold text-foreground">History: </span>
                      {current.clinical_history}
                    </p>
                  ) : (
                    <p className="sk-sit-body mt-0.5 text-foreground-muted">
                      {[current.modality, current.body_part]
                        .filter(Boolean)
                        .join(" · ") || "Case"}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-cy="btn-prev-case"
                    disabled={caseIndex <= 0}
                    onClick={() => {
                      const prev = cases[caseIndex - 1];
                      if (prev) void pickCase(prev);
                    }}
                    className="sk-sit-row rounded-lg border border-border-strong bg-background-card px-3 py-2 text-foreground hover:bg-table-row-hover disabled:opacity-40"
                  >
                    Previous case
                  </button>
                  <button
                    type="button"
                    data-cy="btn-next-case"
                    disabled={caseIndex < 0 || caseIndex >= cases.length - 1}
                    onClick={() => {
                      const next = cases[caseIndex + 1];
                      if (next) void pickCase(next);
                    }}
                    className="sk-sit-row rounded-lg border border-border-strong bg-background-card px-3 py-2 text-foreground hover:bg-table-row-hover disabled:opacity-40"
                  >
                    Next case
                  </button>
                </div>
              </div>

              {/* THE READING ROOM, embedded, taking every pixel the column
                  has left (decided 2026-10-07; docs/scope-reading-room.md
                  section 11). The series list on the left is driven
                  through `onSeries`; a case holding only JPEGs shows the
                  old viewer exactly as before. "Focus" -- or the room's
                  own Full screen -- takes the room over the page and docks
                  the report row and the clock on the right. */}
              <div className="flex min-h-[280px] flex-1 flex-col overflow-hidden rounded-xl border border-border bg-background-card">
                <RoomViewer
                  source={{
                    kind: "attempt",
                    attemptId,
                    assignmentCaseId: current.assignment_case_id,
                  }}
                  testCase={{
                    title: current.title,
                    modality: current.modality,
                    body_part: current.body_part,
                    clinical_history: current.clinical_history,
                  }}
                  showSeriesRail={false}
                  onSeries={setRail}
                  caseNav={{
                    position: caseIndex + 1,
                    count: cases.length,
                    onPrev: () => {
                      const prev = cases[caseIndex - 1];
                      if (prev) void pickCase(prev);
                    },
                    onNext: () => {
                      const next = cases[caseIndex + 1];
                      if (next) void pickCase(next);
                    },
                  }}
                  full={focus}
                  onFullChange={setFocus}
                  dockSide="right"
                  onDock={setDock}
                  onDockBar={setDockBar}
                  dockLabel="Your report"
                  dockStrip={
                    <span className="rr-strip-clock" data-cy="dock-strip-clock">
                      {formatCountdown(countdown.secondsRemaining)}
                    </span>
                  }
                />
              </div>

              {/* ---- Your report ---------------------------------------- */}
              {/* MOVED, NOT COPIED, into the reading room's dock when it is
                  full screen: a second TestRecorder would be a second
                  recorder. The same instance draws as a row here and as a
                  card in the dock. */}
              <Movable to={dockBar ?? dock}>
                {docked && (
                  <section
                    data-cy="dock-clock"
                    className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background-card px-4 py-3"
                  >
                    <p className="text-[0.75rem] font-semibold uppercase tracking-[0.08em] text-foreground-muted">
                      {inDryRun ? "Practice time" : "Time left"}
                    </p>
                    <TestCountdown
                      secondsRemaining={countdown.secondsRemaining}
                      muted={inDryRun}
                    />
                  </section>
                )}
                <section
                  data-cy="report-bar"
                  className={[
                    "rounded-xl border border-border bg-background-card",
                    docked ? "p-4" : "px-3.5 py-2.5",
                  ].join(" ")}
                >
                  {docked && (
                    <>
                      <p className="sk-sit-eyebrow text-foreground-muted">
                        Your report
                      </p>
                      <p className="sk-sit-strong mb-3 mt-1 text-foreground">
                        {current.title}
                      </p>
                    </>
                  )}
                  <TestRecorder
                    attemptId={attemptId}
                    assignmentCaseId={current.assignment_case_id}
                    hasRecording={current.answered}
                    recordingSeconds={current.recording_seconds}
                    onRecorded={() => {
                      void handleRecorded(current.assignment_case_id);
                    }}
                    onDeleted={() => {
                      void attempt.refetch();
                    }}
                    variant={docked ? "panel" : "bar"}
                    trailing={
                      <>
                        {/* A NUDGE, NOT A GATE. Recording is still allowed
                            with series unopened -- a candidate may decide a
                            sequence adds nothing -- but nothing else would
                            tell them they had missed one. */}
                        {rail && rail.unseen > 0 && !current.answered && (
                          <span
                            data-cy="unopened-warning"
                            className="sk-sit-meta rounded-full border border-status-wip-border bg-status-wip-bg px-2.5 py-1 font-semibold text-status-wip-strong"
                          >
                            {rail.unseen}{" "}
                            series not opened
                          </span>
                        )}
                        <span className="sk-rbar-hint">
                          <b>1</b> Findings <span aria-hidden>→</span> <b>2</b> Impression
                        </span>
                        <button
                          type="button"
                          data-cy="btn-report-guide"
                          aria-expanded={showGuide}
                          onClick={() => setShowGuide((g) => !g)}
                          title={showGuide ? "Hide the report guide" : "Show the report guide"}
                          aria-label={showGuide ? "Hide the report guide" : "Show the report guide"}
                          className="sk-rbar-icon hover:bg-table-row-hover hover:text-foreground"
                        >
                          {showGuide ? (
                            <ChevronDown aria-hidden className="h-4 w-4" />
                          ) : (
                            <ChevronUp aria-hidden className="h-4 w-4" />
                          )}
                        </button>
                      </>
                    }
                  />
                  {/* WHAT TO SAY, in the place they are about to say it.
                      Two named parts rather than "record your report":
                      somebody who has never sat one of these does not know
                      whether an impression is wanted. Always in the dock,
                      where there is room; behind the chevron on the row. */}
                  {(docked || showGuide) && (
                    <div
                      data-cy="report-guide"
                      className={docked ? "mt-3" : "mt-2.5 border-t border-border pt-2.5"}
                    >
                      <p className="sk-sit-body text-foreground">
                        Speak your report aloud, in two parts:{" "}
                        <strong className="font-bold text-foreground">Findings</strong>{" "}
                        — what you see, then{" "}
                        <strong className="font-bold text-foreground">Impression</strong>{" "}
                        — what it means. Recording again replaces the take
                        you have.
                      </p>
                      {docked && rail && rail.unseen > 0 && !current.answered && (
                        <p className="sk-sit-note mt-2.5 rounded-lg border border-status-wip-border bg-status-wip-bg px-3 py-2 text-status-wip-strong">
                          {rail.unseen}{" "}
                          {rail.unseen === 1 ? "series has" : "series have"} not
                          been opened yet. You can still record, but it is
                          worth a look first.
                        </p>
                      )}
                    </div>
                  )}
                </section>
              </Movable>
            </>
          ) : (
            <p
              data-cy="no-case-selected"
              className="rounded-xl border border-border bg-background-card p-6 text-sm text-foreground-muted"
            >
              Choose a case from the list to begin. You can work through them in
              any order.
            </p>
          )}
        </div>
      </section>

      {/* ---- the irreversible pair, and the one that is not ---------- */}

      {/* BEFORE YOU SUBMIT — the sheet, not a sentence.
          It used to name a COUNT: "3 of 9 cases have no recording yet."
          That tells a candidate they are short and not WHICH, so the way
          to find out was to close the dialog and read down the list.
          Every case is named here with its state, and the ones still
          missing a report are the ones the eye lands on.

          AND SUBMIT IS INERT UNTIL EVERY CASE HAS ONE. Submitting is
          how a candidate says "this is finished", and it cannot mean
          that with cases unreported. The way out of a sitting they do
          not intend to finish is "Leave without submitting", which says
          what it does; running out of time expires it, and an expired
          sitting is reviewed with whatever was recorded. */}
      <AppDialog
        open={confirming === "submit"}
        cy="dialog-submit"
        tone="danger"
        title="Before you submit"
        body={
          <>
            <p data-cy="submit-summary">
              {unrecorded === 0
                ? "Every case has a recorded report. Check the list below, then submit."
                : `You have recorded ${recordedCount} of ${cases.length} ${
                    cases.length === 1 ? "report" : "reports"
                  }. Cases highlighted below are still missing one.`}
            </p>
            <ul
              data-cy="submit-case-list"
              className="mt-3 max-h-[46vh] overflow-y-auto rounded-lg border border-border"
            >
              {cases.map((row) => (
                <li
                  key={row.assignment_case_id}
                  data-cy="submit-case-row"
                  data-answered={row.answered ? "true" : "false"}
                  className={[
                    "flex items-center gap-3 border-b border-border px-3 py-2.5 last:border-b-0",
                    row.answered ? "" : "bg-status-wip-bg",
                  ].join(" ")}
                >
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                    {row.position + 1}. {row.title}
                  </span>
                  <span
                    className={[
                      "shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold",
                      row.answered
                        ? "bg-status-done-bg text-status-done"
                        : "bg-status-wip-bg text-status-wip-strong",
                    ].join(" ")}
                  >
                    {row.answered ? "Report recorded" : "Not recorded yet"}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-foreground-muted">
              Submitting ends the exam. You will not be able to change your
              reports afterwards.
            </p>
          </>
        }
        onDismiss={() => setConfirming(null)}
        actions={[
          {
            label: "Keep working",
            cy: "btn-cancel-submit",
            variant: "ghost",
            autoFocus: true,
            onClick: () => setConfirming(null),
          },
          {
            label: "Submit assessment",
            cy: "btn-confirm-submit",
            variant: "danger",
            disabled: unrecorded > 0 || busy,
            onClick: () => {
              setConfirming(null);
              void doSubmit();
            },
          },
        ]}
      />

      <AppDialog
        open={confirming === "exit"}
        cy="dialog-exit"
        tone="danger"
        title="Leave the assessment?"
        body="This ENDS it. You cannot come back to it later, and any cases you have not recorded will stay unanswered."
        onDismiss={() => setConfirming(null)}
        actions={[
          {
            label: "Leave and end it",
            cy: "btn-confirm-exit",
            variant: "danger",
            onClick: () => {
              setConfirming(null);
              void doExit();
            },
          },
          {
            label: "Stay",
            cy: "btn-cancel-exit",
            variant: "ghost",
            autoFocus: true,
            onClick: () => setConfirming(null),
          },
        ]}
      />

      {/* Recorded — where next?
          The actions differ on the LAST case because "next" has no
          meaning there, and a candidate who has just finished the final
          case is at the one moment the two things they might want are
          "check my earlier ones" and "hand it in". Offering those here
          saves hunting for a submit button in the rail. */}
      <AppDialog
        open={justRecorded !== null}
        cy="dialog-recorded"
        tone="success"
        title={inDryRun ? "Practice recording saved" : "Recording saved"}
        body={
          nextCase
            ? `${justRecorded?.title} is recorded. ${recordedCount} of ${cases.length} done.`
            : recordedCount === cases.length
              ? `That was the last one — all ${cases.length} cases are recorded.`
              : `That was the last case in the list. ${unrecorded} still ${
                  unrecorded === 1 ? "has" : "have"
                } no recording.`
        }
        onDismiss={() => setRecordedFor(null)}
        actions={
          nextCase
            ? [
                {
                  label: `Next: ${truncate(nextCase.title)}`,
                  cy: "btn-recorded-next",
                  variant: "primary",
                  autoFocus: true,
                  onClick: () => {
                    setRecordedFor(null);
                    void pickCase(nextCase);
                  },
                },
                {
                  label: "Stay on this case",
                  cy: "btn-recorded-stay",
                  variant: "ghost",
                  onClick: () => setRecordedFor(null),
                },
              ]
            : [
                {
                  label: "Review from the first case",
                  cy: "btn-recorded-review",
                  variant: "primary",
                  autoFocus: true,
                  onClick: () => {
                    setRecordedFor(null);
                    if (cases[0]) void pickCase(cases[0]);
                  },
                },
                // In practice there is nothing to submit; the equivalent
                // ending is finishing the run, which returns to the gate.
                inDryRun
                  ? {
                      label: "Finish practice",
                      cy: "btn-recorded-finish-practice",
                      onClick: () => {
                        setRecordedFor(null);
                        void act(
                          () => finishDryRun(attemptId, false),
                          "Could not end the practice run",
                        );
                      },
                    }
                  : {
                      label: "Submit assessment",
                      cy: "btn-recorded-submit",
                      onClick: () => {
                        setRecordedFor(null);
                        setConfirming("submit");
                      },
                    },
                {
                  label: "Stay here",
                  cy: "btn-recorded-stay",
                  variant: "ghost",
                  onClick: () => setRecordedFor(null),
                },
              ]
        }
      />
    </main>
  );
}

/** Keep a dialog button on one line. Case titles can be long. */
function truncate(s: string, max = 28): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/**
 * Where the candidate is in the sitting.
 *
 * Three phases at most -- the application, the practice run, then the
 * assessment -- and the practice run is OPTIONAL per exam, so the total
 * is 2 or 3 rather than a constant. An exam with no practice that
 * announced "Step 1 of 3" would be promising a step that never comes.
 *
 * Deliberately absent from the START GATE. That screen is not a step;
 * it is the junction where the candidate chooses between practising and
 * beginning, and either number written there would be wrong for one of
 * the two buttons.
 */
function StepBadge({ step, total }: { step: number; total: number }) {
  return (
    <span
      data-cy="step-badge"
      className="shrink-0 rounded-full bg-foreground-on-chrome/10 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide"
    >
      Step {step} of {total}
    </span>
  );
}

function PlainHeader({
  title,
  step,
  total,
}: {
  title: string;
  step?: number;
  total?: number;
}) {
  return (
    <header
      data-cy="topbar"
      className="flex items-center gap-4 bg-background-topbar px-6 py-3 text-foreground-on-chrome shadow-theme-sm"
    >
      <OrgBrand variant="topbar" />
      <span className="truncate text-sm font-medium">{title}</span>
      {step && total ? <StepBadge step={step} total={total} /> : null}
    </header>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main
      data-cy="test-attempt-page"
      className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground"
    >
      {children}
    </main>
  );
}
